import { validateFormValue, setFieldError, clearFieldError } from './formValidation';

const NON_BLUEPRINT = '.newc-array-row:not(.newc-array-row-blueprint)';

// Valida un campo (o sotto-campo) e mostra/toglie il messaggio. Ritorna true se
// c'è un errore, così chi chiama sa che il submit va fermato.
function reportField(el, value, labels) {
  const message = validateFormValue({
    type: el.dataset.type,
    required: el.required,
    value,
    min: el.getAttribute('min'),
    max: el.getAttribute('max'),
  }, labels);
  if (message) { setFieldError(el, message); return true; }
  clearFieldError(el);
  return false;
}

function readField(el) {
  if (el.dataset.type === 'checkbox') return el.checked;
  if (el.dataset.type === 'address') {
    const raw = String(el.value || '').trim();
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  }
  return el.value;
}

// I sotto-campi di una riga vanno validati come quelli di primo livello: senza,
// una riga con articolo o quantità vuoti passava il controllo e finiva al webhook.
function collectArrayContainer(container, labels) {
  const shape = container.dataset.shape || 'object';
  const rows = container.querySelectorAll(NON_BLUEPRINT);
  const arr = [];
  let invalid = false;
  const check = (el, value) => {
    // per l'indirizzo conta solo se una selezione c'è o non c'è
    const probe = el.dataset.type === 'address' ? (value ? 'x' : '') : value;
    if (reportField(el, probe, labels)) invalid = true;
  };
  rows.forEach(row => {
    const fields = row.querySelectorAll('.newc-array-field');
    if (shape === 'scalar') {
      if (fields[0]) { const v = readField(fields[0]); check(fields[0], v); arr.push(v); }
    } else {
      const obj = {};
      fields.forEach(el => { const v = readField(el); check(el, v); obj[el.dataset.name] = v; });
      arr.push(obj);
    }
  });
  return { arr, invalid };
}

function refreshArrayControls(container) {
  const rowsWrap = container.querySelector('.newc-array-rows');
  if (!rowsWrap) return;
  const visibleRows = rowsWrap.querySelectorAll(NON_BLUEPRINT);
  const count = visibleRows.length;
  const maxVal = container.dataset.max;
  // data-max "" o "0" = nessun limite (convenzione dell'intera feature; il backend emette data-max="" per illimitato)
  const max = !maxVal || maxVal === '0' ? Infinity : Number(maxVal);
  const addBtn = container.querySelector('.newc-array-add');
  if (addBtn) {
    addBtn.disabled = count >= max;
    if (count >= max) {
      addBtn.style.opacity = '0.5';
      addBtn.style.cursor = 'not-allowed';
    } else {
      addBtn.style.opacity = '';
      addBtn.style.cursor = 'pointer';
    }
  }
  const errEl = container.querySelector('.newc-array-error');
  if (errEl) {
    errEl.textContent = '';
    errEl.style.display = 'none';
  }
}

export function collectFormValues(form, labels) {
  const values = {};
  let missing = false;

  form.querySelectorAll('.newc-form-field').forEach(el => {
    const name = el.dataset.name;
    if (el.dataset.type === 'checkbox') {
      values[name] = el.checked;
      if (reportField(el, el.checked, labels)) missing = true;
      return;
    }
    if (el.dataset.type === 'address') {
      const raw = String(el.value || '').trim();
      let obj = null;
      if (raw) { try { obj = JSON.parse(raw); } catch { obj = null; } }
      values[name] = obj;
      const wrap = el.closest('.newc-form-address');
      const civicoRow = wrap && wrap.querySelector('.newc-form-address-civico');
      const civicoError = wrap && wrap.querySelector('.newc-form-address-civico-error');
      if (el.required && !obj) {
        missing = true;
        setFieldError(el, (labels && labels.required) || 'Required field');
      } else if (obj && !String(obj.number || '').trim()) {
        missing = true;                                   // a selected address must have a civic number
        clearFieldError(el);
        if (civicoRow) { civicoRow.style.display = 'flex'; civicoRow.classList.add('newc-form-invalid'); }
        if (civicoError) civicoError.style.display = 'block';
      } else {
        clearFieldError(el);
        if (civicoRow) civicoRow.classList.remove('newc-form-invalid');
        if (civicoError) civicoError.style.display = 'none';
      }
      return;
    }
    values[name] = el.value;
    if (reportField(el, el.value, labels)) missing = true;
  });

  form.querySelectorAll('.newc-form-array').forEach(container => {
    const name = container.dataset.name;
    const min = Number(container.dataset.min) || 0;
    const maxVal = container.dataset.max;
    // data-max "" o "0" = nessun limite (convenzione dell'intera feature; il backend emette data-max="" per illimitato)
    const max = !maxVal || maxVal === '0' ? Infinity : Number(maxVal);
    const required = container.dataset.required === 'true';
    const { arr, invalid } = collectArrayContainer(container, labels);
    if (invalid) missing = true;
    const floor = Math.max(min, required ? 1 : 0);
    const errEl = container.querySelector('.newc-array-error');
    if (arr.length < floor || arr.length > max) {
      container.classList.add('newc-form-invalid');
      missing = true;
      if (errEl) {
        let message;
        if (arr.length < floor) {
          message = floor === 1 ? 'Aggiungi almeno un elemento.' : 'Aggiungi almeno ' + floor + ' elementi.';
        } else {
          message = 'Massimo ' + max + ' elementi.';
        }
        errEl.textContent = message;
        errEl.style.display = 'block';
      }
    } else {
      container.classList.remove('newc-form-invalid');
      if (errEl) {
        errEl.textContent = '';
        errEl.style.display = 'none';
      }
    }
    values[name] = arr;
  });

  return { values, missing };
}

export function addArrayRow(addBtn) {
  const container = addBtn.closest('.newc-form-array');
  if (!container) return;
  const maxVal = container.dataset.max;
  // data-max "" o "0" = nessun limite (convenzione dell'intera feature; il backend emette data-max="" per illimitato)
  const max = !maxVal || maxVal === '0' ? Infinity : Number(maxVal);
  const rowsWrap = container.querySelector('.newc-array-rows');
  const current = rowsWrap.querySelectorAll(NON_BLUEPRINT).length;
  if (current >= max) return;
  const blueprint = container.querySelector('.newc-array-row-blueprint');
  if (!blueprint) return;
  const clone = blueprint.cloneNode(true);
  clone.classList.remove('newc-array-row-blueprint');
  // Blueprint inline style ends with "display:none;" — setting '' would fall back to
  // block and lose the row layout. Restore the base display: object rows use a
  // responsive grid, scalar rows a flex row.
  clone.style.display = container.dataset.shape === 'scalar' ? 'flex' : 'grid';
  clone.querySelectorAll('.newc-array-field').forEach(el => {
    if (el.type === 'checkbox') el.checked = false; else el.value = '';
  });
  rowsWrap.appendChild(clone);
  refreshArrayControls(container);
}

export function removeArrayRow(removeBtn) {
  const container = removeBtn.closest('.newc-form-array');
  const row = removeBtn.closest('.newc-array-row');
  if (!container || !row) return;
  const min = Number(container.dataset.min) || 0;
  const required = container.dataset.required === 'true';
  const floor = Math.max(min, required ? 1 : 0);
  const rowsWrap = container.querySelector('.newc-array-rows');
  const visible = rowsWrap.querySelectorAll(NON_BLUEPRINT).length;
  if (visible <= floor) return;
  row.remove();
  refreshArrayControls(container);
}
